import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Paper from '@mui/material/Paper';
import { useTheme } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { QRCodeSVG } from 'qrcode.react';
import { useState } from 'react';
import { Footer, Header } from '@/core/components';
import { useRace } from '@/core/contexts/RaceContext';
import { useFavicon } from '@/core/hooks';
import { getRunnerByAliasBib } from '@/core/services/claim';
import type { Runner } from '@/core/types/admin';

type ViewState = 'input' | 'loading' | 'result' | 'not-found' | 'no-timing';

export function BoothPage() {
  const theme = useTheme();
  const { race } = useRace();
  useFavicon(`/races/${race.alias}/favicon.ico`);
  const gradient = theme.custom?.gradient || theme.palette.primary.main;
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const [bib, setBib] = useState('');
  const [viewState, setViewState] = useState<ViewState>('input');
  const [runner, setRunner] = useState<Runner | null>(null);
  const [error, setError] = useState<string | null>(null);

  // 全角数字を半角に変換
  const toHalfWidth = (str: string): string => {
    return str.replace(/[０-９]/g, (s) => String.fromCharCode(s.charCodeAt(0) - 65248));
  };

  // 検索実行
  const handleSearch = async () => {
    const normalizedBib = toHalfWidth(bib).trim();
    if (!normalizedBib) {
      setError('ゼッケン番号を入力してください');
      return;
    }

    setError(null);
    setViewState('loading');

    try {
      const result = await getRunnerByAliasBib(race.alias, normalizedBib);

      if (!result) {
        setViewState('not-found');
        return;
      }

      if (!result.timingHash) {
        setViewState('no-timing');
        return;
      }

      setRunner(result);
      setViewState('result');
    } catch (e) {
      console.error(e);
      setError('検索中にエラーが発生しました');
      setViewState('input');
    }
  };

  // 結果画面を閉じる
  const handleClose = () => {
    setBib('');
    setRunner(null);
    setViewState('input');
    setError(null);
  };

  // QRコードのURL生成
  const getClaimUrl = () => {
    if (!runner?.timingHash) return '';
    const baseUrl = window.location.origin;
    return `${baseUrl}/${race.alias}/claim?code=${runner.timingHash}`;
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100dvh', overflow: 'hidden' }}>
      <Header titleImage={`/races/${race.alias}/title.png`} titleImageSm={`/races/${race.alias}/title_sm.png`} />

      <Container
        component="main"
        maxWidth="sm"
        sx={{ flex: 1, display: 'flex', flexDirection: 'column', py: { xs: 1, sm: 2 }, overflow: 'hidden' }}
      >
        {/* 入力画面 */}
        {(viewState === 'input' || viewState === 'loading') && (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              gap: 3,
              textAlign: 'center',
            }}
          >
            {error && (
              <Alert severity="error" sx={{ width: '100%' }}>
                {error}
              </Alert>
            )}

            <TextField
              value={bib}
              onChange={(e) => setBib(toHalfWidth(e.target.value))}
              placeholder="ゼッケン番号を入力"
              sx={{ width: '100%' }}
              inputProps={{
                style: { fontSize: isSmallScreen ? '1.5rem' : '2rem', textAlign: 'center', fontWeight: 'bold' },
                inputMode: 'numeric',
              }}
              autoFocus
            />

            <Button
              variant="contained"
              size="large"
              onClick={handleSearch}
              disabled={!bib || viewState === 'loading'}
              sx={{
                width: '100%',
                py: { xs: 1.5, sm: 2 },
                fontSize: '1.2rem',
                fontWeight: 'bold',
                background: gradient,
                '&:hover': {
                  background: gradient,
                  filter: 'brightness(0.9)',
                },
              }}
            >
              {viewState === 'loading' ? '検索中...' : 'QRコード表示'}
            </Button>
          </Box>
        )}

        {/* 結果画面（QRコード表示） */}
        {viewState === 'result' && runner && (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-evenly',
              alignItems: 'center',
              textAlign: 'center',
            }}
          >
            <Box>
              <Typography
                variant="h5"
                sx={{
                  mb: 1,
                  fontWeight: 700,
                  background: gradient,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                完走おめでとうございます！
              </Typography>
              <Typography variant="body1" color="text.secondary">
                ゼッケン番号: {runner.bib}
              </Typography>
              {runner.fullName_katakana && (
                <Typography variant="body2" color="text.secondary">
                  {runner.fullName_katakana}
                </Typography>
              )}
              <Typography variant="h4" fontWeight="bold">
                {runner.fullName || 'ランナー'} さん
              </Typography>
            </Box>

            <Paper
              elevation={3}
              sx={{
                p: { xs: 2, sm: 3 },
                display: 'inline-block',
                backgroundColor: '#fff',
              }}
            >
              <QRCodeSVG value={getClaimUrl()} size={isSmallScreen ? 160 : 200} level="H" />
            </Paper>

            <Button
              variant="contained"
              size="large"
              onClick={handleClose}
              sx={{
                width: '100%',
                py: { xs: 1.5, sm: 2 },
                fontSize: '1.2rem',
                fontWeight: 'bold',
                background: gradient,
                '&:hover': {
                  background: gradient,
                  filter: 'brightness(0.9)',
                },
              }}
            >
              クリア
            </Button>
          </Box>
        )}

        {/* 見つからない場合 */}
        {viewState === 'not-found' && (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              textAlign: 'center',
            }}
          >
            <Alert severity="warning" sx={{ mb: 3 }}>
              ゼッケン番号 {bib} の完走記録が見つかりません
            </Alert>
            <Button
              variant="contained"
              onClick={handleClose}
              sx={{
                py: 1.5,
                px: 4,
                background: gradient,
                '&:hover': {
                  background: gradient,
                  filter: 'brightness(0.9)',
                },
              }}
            >
              戻る
            </Button>
          </Box>
        )}

        {/* 計測データなし */}
        {viewState === 'no-timing' && (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              textAlign: 'center',
            }}
          >
            <Alert severity="info" sx={{ mb: 3 }}>
              ゼッケン番号 {bib} の計測データがまだ登録されていません
            </Alert>
            <Button
              variant="contained"
              onClick={handleClose}
              sx={{
                py: 1.5,
                px: 4,
                background: gradient,
                '&:hover': {
                  background: gradient,
                  filter: 'brightness(0.9)',
                },
              }}
            >
              戻る
            </Button>
          </Box>
        )}
      </Container>

      <Footer />
    </Box>
  );
}
